/**
 * ContractWizardModal — Wizard de emissão de contrato em 4 steps.
 * Reutilizado em: ContratosPage (novo contrato) e NegocioDetailModal (ganho).
 * 
 * Props:
 *   open, onClose — controle do modal
 *   prefillNegocioId, prefillTemplateId — pré-preenchimento (pula step 1)
 *   onContractSent(result) — callback após contrato ser enviado com sucesso
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { usePermissions } from "../hooks/usePermissions";
import {
  FileSignature, Plus, Trash2, Search, Check, X, ChevronDown,
  Loader2, Mail, MessageCircle, Send, Eye, Brain, Info, Folder,
  Calendar, UserCircle, ArrowLeft, ArrowRight, RefreshCw, Pencil, AlertCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import AIReviewModal from "./AIReviewModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import { useAppearance } from "../contexts/AppearanceContext";
import { applyMask } from "./PhoneInput";
import { JsonRenderer } from "./negocio-detail/NegocioDialogs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractWizardModal({ open, onClose, prefillNegocioId, prefillTemplateId, stageTemplates, onContractSent }) {
  const appearance = useAppearance();
  const { can } = usePermissions();
  const tc500 = appearance.colors?.["500"] || "#6366f1";
  const tc50 = appearance.colors?.["50"] || "#eef2ff";
  const phoneMask = appearance.phone_mask || "(##) #####-####";

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ template_id: "", negocio_id: "" });
  const [signers, setSigners] = useState([{ name: "", channel: "email", email: "", phone: "" }]);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [templateConfig, setTemplateConfig] = useState(null);
  const [docUploads, setDocUploads] = useState({});
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiValidating, setAiValidating] = useState(false);
  const [aiDetailOpen, setAiDetailOpen] = useState(false);
  const [editingCnpj, setEditingCnpj] = useState(false);
  const [cnpjOverride, setCnpjOverride] = useState("");
  const [validatingCnpj, setValidatingCnpj] = useState(false);
  const [cnpjModalOpen, setCnpjModalOpen] = useState(false);
  const [cnpjModalError, setCnpjModalError] = useState("");
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [negocioData, setNegocioData] = useState(null);
  const [negocioSearchTerm, setNegocioSearchTerm] = useState("");
  const [negocioPopoverOpen, setNegocioPopoverOpen] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [filePickerIdx, setFilePickerIdx] = useState(null);
  const [filePickerList, setFilePickerList] = useState([]);

  const hasValidation = templateConfig && (templateConfig.required_documents?.length > 0 || templateConfig.ai_validation_enabled);

  // Load data when opening
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [tR, nR, cR, eR, aR] = await Promise.all([
          axios.get(`${API}/autentique/templates`),
          axios.get(`${API}/negocios?limit=200&skip=0&status_filter=ganho&include_finalized=true&sort_by=ganho_em&sort_dir=desc`).catch(() => ({ data: [] })),
          axios.get(`${API}/contatos`).catch(() => ({ data: [] })),
          axios.get(`${API}/empresas`).catch(() => ({ data: [] })),
          axios.get(`${API}/autentique/account-info`).catch(() => ({ data: {} })),
        ]);
        setTemplates(tR.data || []);
        const negList = nR.data?.data || nR.data || [];
        setNegocios(negList);
        setContatos(Array.isArray(cR.data) ? cR.data : cR.data?.data || []);
        setEmpresas(Array.isArray(eR.data) ? eR.data : eR.data?.data || []);
        setOwnerEmail(aR.data?.email || "");

        // If prefilled, load template + skip to step 2
        if (prefillNegocioId && prefillTemplateId) {
          setForm({ template_id: prefillTemplateId, negocio_id: prefillNegocioId });
          const tplRes = await axios.get(`${API}/autentique/templates/${prefillTemplateId}`);
          setTemplateConfig(tplRes.data);
          if (tplRes.data.signers?.length > 0) {
            setSigners(tplRes.data.signers.map(s => ({
              name: s.name || "", channel: s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? "whatsapp" : "email",
              email: s.email || "", phone: s.phone || "", _fromTemplate: true,
            })));
          }
          // Pre-fill from contact — always fetch deal for full data
          let contactForPrefill = null;
          try {
            const negRes = await axios.get(`${API}/negocios/${prefillNegocioId}`, { withCredentials: true });
            setNegocioData(negRes.data);
            if (negRes.data?.contato_id) {
              contactForPrefill = (Array.isArray(cR.data) ? cR.data : cR.data?.data || []).find(c => c.id === negRes.data.contato_id);
            }
            // Load last validation — but invalidate if files were deleted
            const lv = negRes.data?.last_ai_validation;
            if (lv?.validated_at) {
              let validationStillValid = true;
              if (lv.file_ids?.length) {
                const filesRes = await axios.get(`${API}/files/list?negocio_id=${prefillNegocioId}`);
                const allFiles = filesRes.data || [];
                const uploads = {};
                let foundCount = 0;
                lv.file_ids.forEach((fid, i) => {
                  const f = allFiles.find(af => af.id === fid);
                  if (f) { uploads[i] = { ...f, _existing: true, name: f.original_filename }; foundCount++; }
                });
                // If any validated file was deleted, invalidate the whole validation
                if (foundCount < lv.file_ids.length) {
                  validationStillValid = false;
                  setAiResult(null);
                  setDocUploads({});
                } else {
                  setDocUploads(uploads);
                }
              }
              if (validationStillValid) {
                const hasReviewed = !!negRes.data?.ai_reviewed_data?.empresa;
                setAiResult({ approved: lv.approved, reason: lv.reason, data: lv.data || {}, validated_at: lv.validated_at, validated_by: lv.validated_by, _cached: true, _pendingReview: lv.approved && !hasReviewed });
              }
            }
          } catch {}
          // Prefill signers from AI administrators
          await prefillSignersFromAI(prefillNegocioId);
          setStep(tplRes.data.required_documents?.length > 0 || tplRes.data.ai_validation_enabled ? 1 : 2);
        } else if (stageTemplates?.length > 1 && prefillNegocioId) {
          // Multiple templates for this stage — show Step 1 with card selection, negocio already set
          setForm({ template_id: "", negocio_id: prefillNegocioId });
          setSigners([{ name: "", channel: "email", email: "", phone: "" }]);
          setTemplateConfig(null);
          setDocUploads({});
          setAiResult(null);
          try {
            const negRes = await axios.get(`${API}/negocios/${prefillNegocioId}`, { withCredentials: true });
            setNegocioData(negRes.data);
          } catch {}
          setStep(0);
        } else {
          setForm({ template_id: "", negocio_id: "" });
          setSigners([{ name: "", channel: "email", email: "", phone: "" }]);
          setTemplateConfig(null);
          setDocUploads({});
          setAiResult(null);
          setStep(0);
        }
      } catch {}
    })();
  }, [open, prefillNegocioId, prefillTemplateId]); // eslint-disable-line

  const toTitleCase = (str) => (str || "").toLowerCase().replace(/(?:^|\s)\S/g, c => c.toUpperCase());

  const prefillSignersFromAI = async (negId) => {
    if (!negId) return;
    try {
      const negRes = await axios.get(`${API}/negocios/${negId}`);
      const aiData = negRes.data?.ai_validation_data || negRes.data?.last_ai_validation?.data || {};
      const dados = aiData.dados_extraidos || {};
      const cs = dados.contrato_social || {};
      const admins = cs.administradores || [];
      if (admins.length > 0) {
        const aiSigners = admins.map(a => ({
          name: toTitleCase(a.nome || ""),
          email: "",
          phone: "",
          channel: "email",
          _fromAI: true,
        }));
        setSigners(prev => {
          const templateSigners = prev.filter(s => s._fromTemplate);
          const cleaned = templateSigners.length > 0 ? templateSigners : [];
          if (cleaned.length === 0 && prev.length === 1 && !prev[0].name && !prev[0].email) return aiSigners;
          return [...cleaned, ...aiSigners];
        });
        return;
      }
    } catch {}
    // Fallback: no AI data, leave empty signer for manual input
  };

  const prefillSignerFromContact = (ct) => {
    let email = ct?.email || "";
    let phone = "";
    for (const tc of ct?.tipos_contato || []) {
      if (!email && ["e-mail", "email"].includes((tc.tipo || "").toLowerCase())) email = tc.valor;
      if (!phone && ["celular", "whatsapp", "telefone"].includes((tc.tipo || "").toLowerCase())) phone = (tc.valor || "").replace(/\D/g, "");
    }
    if (!ct?.nome && !email && !phone) return;
    const contactSigner = {
      name: ct?.nome || "",
      email: email,
      phone: phone,
      channel: phone && !email ? "whatsapp" : "email",
      _fromContact: true,
    };
    setSigners(prev => {
      // Remove any previous contact-based signer
      const templateSigners = prev.filter(s => !s._fromContact);
      // If no template signers and only an empty default, replace it
      if (templateSigners.length === 1 && !templateSigners[0].name && !templateSigners[0].email && !templateSigners[0].phone) {
        return [contactSigner];
      }
      // Otherwise append the contact signer
      return [...templateSigners, contactSigner];
    });
  };

  const onNegocioSelect = (negId) => {
    if (negId === "none") { setForm(f => ({ ...f, negocio_id: "" })); setNegocioData(null); return; }
    const neg = negocios.find(n => n.id === negId);
    setForm(f => ({ ...f, negocio_id: negId }));
    setNegocioData(neg || null);
    if (neg) {
      prefillSignersFromAI(negId);
    }
  };

  const loadTemplate = async (tplId) => {
    setTemplateConfig(null); setDocUploads({}); setAiResult(null);
    if (!tplId) return;
    try {
      const r = await axios.get(`${API}/autentique/templates/${tplId}`);
      setTemplateConfig(r.data);
      const tplSigners = (r.data.signers || []).map(s => ({ name: s.name || "", channel: s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? "whatsapp" : "email", email: s.email || "", phone: s.phone || "", _fromTemplate: true }));
      setSigners(tplSigners.length > 0 ? tplSigners : [{ name: "", channel: "email", email: "", phone: "" }]);
      // Re-add AI signers if deal already selected
      if (negocioData || form.negocio_id) {
        setTimeout(() => prefillSignersFromAI(form.negocio_id || negocioData?.id), 0);
      }
    } catch {}
  };

  const validateWithAI = async () => {
    if (!form.negocio_id) return;
    setAiValidating(true); setAiResult(null);
    try {
      const docIds = [];
      for (const [, item] of Object.entries(docUploads)) { if (item.id) docIds.push(item.id); }
      const r = await axios.post(`${API}/negocios/${form.negocio_id}/validate-win`, { file_ids: docIds, prompt: templateConfig?.ai_prompt || "" });
      if (!r.data.approved) {
        setDocUploads({});
        setAiResult({ approved: false, reason: r.data.reason || "", data: r.data.data || {}, validated_at: new Date().toISOString() });
      } else {
        // AI approved — open review modal for user to confirm data before saving
        setAiResult({ approved: true, reason: r.data.reason || "", data: r.data.data || {}, validated_at: new Date().toISOString(), _pendingReview: true });
        setAiReviewOpen(true);
      }
    } catch (err) {
      setDocUploads({});
      setAiResult({ approved: false, reason: err.response?.data?.detail || "Erro", data: {} });
    }
    setAiValidating(false);
  };

  const sendContract = async () => {
    const validSigners = signers.filter(s => s.name && (s.channel === "email" ? s.email : s.phone));
    if (!validSigners.length) { toast.error("Adicione signatários."); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/autentique/contracts/send`, { ...form, signers: validSigners, ai_validation_data: aiResult?.data || {} });
      if (onContractSent) await onContractSent(r.data);
      else toast.success("Contrato enviado!");
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    setSending(false);
  };

  const isPrefilled = !!(prefillNegocioId && prefillTemplateId);
  const hasStageTemplates = !!(stageTemplates && stageTemplates.length > 1 && prefillNegocioId && !prefillTemplateId);

  if (!open) return null;

  const neg = negocios.find(n => n.id === form.negocio_id) || negocioData;
  const ct = contatos.find(c => c.id === neg?.contato_id);
  const emp = empresas.find(e => e.id === neg?.empresa_id);

  return createPortal(
    <div className="fixed inset-0 z-[99998] bg-black/60 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header + Timeline */}
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center justify-between mb-5">
            <p className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><FileSignature className="w-5 h-5" style={{ color: tc500 }} />Emissão de Contrato</p>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center w-full px-4">
            {[{ n: 1, l: "Contrato" }, { n: 2, l: "Validação" }, { n: 3, l: "Signatários" }, { n: 4, l: "Resumo" }].map((s, idx) => (
              <div key={s.n} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center" style={{ width: "60px" }}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= idx ? "text-white" : "bg-slate-100 text-slate-400"}`} style={step >= idx ? { backgroundColor: tc500 } : undefined}>
                    {step > idx ? <Check className="w-4 h-4" /> : s.n}
                  </div>
                  <span className={`text-[10px] font-medium mt-1 ${step >= idx ? "text-slate-800" : "text-slate-400"}`}>{s.l}</span>
                </div>
                {idx < 3 && <div className={`h-px flex-1 -mt-4 ${step > idx ? "" : "bg-slate-200"}`} style={step > idx ? { backgroundColor: tc500 } : undefined} />}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-col" style={{ minHeight: "320px" }}>
          {/* Loading when prefilled */}
          {step === 0 && isPrefilled && (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: tc500 }} /></div>
          )}

          {/* Step 1 — skip if prefilled */}
          {step === 0 && !isPrefilled && hasStageTemplates && (
            <div className="space-y-4 flex-1 flex flex-col">
              <div className="flex-1 space-y-3">
                <p className="text-sm text-slate-500">Selecione o modelo de contrato para este negócio:</p>
                <div className="space-y-2">
                  {stageTemplates.map(st => {
                    const selected = form.template_id === st.template_id;
                    return (
                      <button key={st.template_id} data-testid={`stage-template-card-${st.template_id}`}
                        onClick={async () => {
                          setForm(f => ({ ...f, template_id: st.template_id }));
                          await loadTemplate(st.template_id);
                          await prefillSignersFromAI(prefillNegocioId);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${selected ? "border-current shadow-sm" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"}`}
                        style={selected ? { borderColor: tc500, backgroundColor: tc50 } : undefined}>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? "" : "bg-slate-100"}`} style={selected ? { backgroundColor: tc500 + "20" } : undefined}>
                          <FileSignature className="w-5 h-5" style={{ color: selected ? tc500 : "#94a3b8" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${selected ? "text-slate-900" : "text-slate-700"}`}>{st.template_name}</p>
                        </div>
                        {selected && <Check className="w-4 h-4 flex-shrink-0" style={{ color: tc500 }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button className="w-full text-white" style={{ backgroundColor: tc500 }} disabled={!form.template_id} onClick={() => setStep(hasValidation ? 1 : 2)}><span className="flex items-center gap-1">Avançar<ArrowRight className="w-4 h-4" /></span></Button>
            </div>
          )}

          {step === 0 && !isPrefilled && !hasStageTemplates && (
            <div className="space-y-4 flex-1 flex flex-col">
              <div className="flex-1 space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Modelo de contrato</Label>
                  <Select value={form.template_id} onValueChange={v => { setForm(f => ({ ...f, template_id: v })); loadTemplate(v); }}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Selecione o modelo..." /></SelectTrigger>
                    <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex items-center gap-1.5"><Label className="text-sm font-medium text-slate-700">Negócio</Label></div>
                  <Popover open={negocioPopoverOpen} onOpenChange={setNegocioPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full mt-1.5 justify-between font-normal bg-white text-slate-700 hover:bg-white hover:text-slate-700">
                        {form.negocio_id ? neg?.titulo || "Selecione..." : "Selecione o negócio..."}<ChevronDown className="w-4 h-4 text-slate-400 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <div className="p-2 border-b border-slate-200"><div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><Input value={negocioSearchTerm} onChange={e => setNegocioSearchTerm(e.target.value)} placeholder="Buscar..." className="pl-8 h-8 text-sm" /></div></div>
                      <div className="max-h-52 overflow-y-auto p-1" style={{ overscrollBehavior: "contain" }} onWheel={e => e.stopPropagation()}>
                        {negocios.filter(n => !negocioSearchTerm || n.titulo?.toLowerCase().includes(negocioSearchTerm.toLowerCase())).map(n => (
                          <button key={n.id} onClick={() => { onNegocioSelect(n.id); setNegocioPopoverOpen(false); setNegocioSearchTerm(""); }}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors ${form.negocio_id === n.id ? "font-medium" : "text-slate-700"} hover:bg-slate-100`}>
                            <Check className={`w-3.5 h-3.5 flex-shrink-0 ${form.negocio_id === n.id ? "opacity-100" : "opacity-0"}`} style={{ color: tc500 }} />
                            <span className="flex-1 truncate">{n.titulo}</span>
                            <span className="text-[10px] text-slate-400 truncate max-w-[100px]">{n.responsavel_nome || n.user_name || ""}</span>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <Button className="w-full text-white" style={{ backgroundColor: tc500 }} disabled={!form.template_id || !form.negocio_id} onClick={() => setStep(hasValidation ? 1 : 2)}><span className="flex items-center gap-1">Avançar<ArrowRight className="w-4 h-4" /></span></Button>
            </div>
          )}

          {/* Step 2: Validation */}
          {step === 1 && (
            <div className="space-y-4 flex-1 flex flex-col">
              <div className="flex-1 space-y-4">
                {templateConfig?.required_documents?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Documentos obrigatórios</p>
                    {templateConfig.required_documents.map((doc, i) => (
                      <div key={i} className="flex items-center gap-2 p-2.5 bg-white border border-slate-200 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800">{doc.name} <span className="text-rose-500">*</span></p>
                          {docUploads[i] ? <p className="text-[10px] text-emerald-600 flex items-center gap-1 mt-0.5"><Check className="w-2.5 h-2.5" />{docUploads[i].name || docUploads[i].original_filename}<button onClick={() => setDocUploads(p => { const n = {...p}; delete n[i]; return n; })} className="ml-1 text-slate-400 hover:text-rose-500"><X className="w-2.5 h-2.5" /></button></p>
                          : <p className="text-[10px] text-slate-400 mt-0.5">Nenhum arquivo</p>}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <label className={`cursor-pointer px-2 py-1 text-[10px] font-medium border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1 ${uploadingIdx === i ? "opacity-50 pointer-events-none" : ""}`}>{uploadingIdx === i ? <><Loader2 className="w-3 h-3 animate-spin" />Enviando</> : "Upload"}<input type="file" className="hidden" onChange={async e => { const file = e.target.files?.[0]; if (!file) return; setUploadingIdx(i); try { const fd = new FormData(); fd.append("file", file); fd.append("negocio_id", form.negocio_id); fd.append("doc_type", doc.name); const r = await axios.post(`${API}/files/upload`, fd); setDocUploads(p => ({ ...p, [i]: { ...r.data, _existing: true, name: file.name } })); } catch { toast.error("Erro"); } setUploadingIdx(null); }} /></label>
                          <button onClick={async () => { setFilePickerIdx(i); setFilePickerOpen(true); try { const r = await axios.get(`${API}/files/list?negocio_id=${form.negocio_id}`); setFilePickerList(r.data || []); } catch { setFilePickerList([]); } }} className="px-2 py-1 text-[10px] font-medium border border-slate-300 rounded hover:bg-slate-50">Selecionar</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {templateConfig?.ai_validation_enabled && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="text-xs gap-1.5" disabled={aiValidating || (templateConfig?.required_documents?.length > 0 && Object.keys(docUploads).length < templateConfig.required_documents.length)} onClick={validateWithAI}>
                        {aiValidating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}Validar com IA
                      </Button>
                      {aiResult?.approved && (
                        <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => setAiReviewOpen(true)}>
                          <Eye className="w-3 h-3" />Conferir dados
                        </Button>
                      )}
                    </div>
                    {aiResult && !aiValidating && (
                      <div className={`p-3 rounded-lg text-sm border ${aiResult.approved && !aiResult._pendingReview ? "bg-emerald-50 border-emerald-200 text-emerald-800" : aiResult.approved && aiResult._pendingReview ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-semibold flex items-center gap-2">
                            {aiResult.approved && !aiResult._pendingReview && <><Check className="w-4 h-4" />Aprovado e conferido</>}
                            {aiResult.approved && aiResult._pendingReview && <><AlertCircle className="w-4 h-4" />Aguardando conferência</>}
                            {!aiResult.approved && <><X className="w-4 h-4" />Reprovado</>}
                          </p>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setAiDetailOpen(true)} className="w-6 h-6 rounded flex items-center justify-center hover:bg-white/50"><Eye className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        {aiResult.reason && <p className="text-xs mt-2">{aiResult.reason}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 pt-1">
                {!isPrefilled && <button onClick={() => setStep(0)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>}
                <div className="flex-1" />
                {/* Show Aprovado only after review confirmed */}
                {aiResult?.approved && !aiResult._pendingReview && (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium mr-2">
                    <Check className="w-3.5 h-3.5" />Validado e conferido
                  </div>
                )}
                {/* Pending review — show amber indicator */}
                {aiResult?.approved && aiResult._pendingReview && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium mr-2">
                    <AlertCircle className="w-3.5 h-3.5" />Aguardando conferência
                  </div>
                )}
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={templateConfig?.ai_validation_enabled && (!aiResult || !aiResult.approved || aiResult._pendingReview)} onClick={() => setStep(2)}>
                  <span className="flex items-center gap-1">Avançar<ArrowRight className="w-4 h-4" /></span>
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Signers */}
          {step === 2 && (
            <div className="space-y-3 flex-1 flex flex-col">
              <div className="flex-1 space-y-3">
                {signers.map((s, i) => {
                  const locked = !!s._fromTemplate;
                  const aiLocked = !!s._fromAI && !can("contratos", "editar_signatario_ia");
                  const nameLocked = locked || aiLocked;
                  return (
                  <div key={i} className="space-y-2">
                    {locked && i === 0 && <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Do template</p>}
                    {s._fromAI && !signers[i - 1]?._fromAI && <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold pt-2">Responsáveis (IA)</p>}
                    {!locked && !s._fromAI && signers.filter(x => x._fromTemplate || x._fromAI).length > 0 && !signers[i - 1]?._fromTemplate && !signers[i - 1]?._fromAI && <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold pt-2">Adicionais</p>}
                    <div className="flex items-center gap-2">
                      <Input value={s.name} onChange={e => { if (nameLocked) return; const u = [...signers]; u[i] = { ...u[i], name: e.target.value }; setSigners(u); }} placeholder="Nome completo" className={`flex-1 h-9 text-sm ${nameLocked ? "bg-slate-50 text-slate-500" : "bg-white"}`} readOnly={nameLocked} />
                      <button onClick={() => { if (locked) return; const u = [...signers]; u[i] = { ...u[i], channel: s.channel === "email" ? "whatsapp" : "email" }; setSigners(u); }}
                        disabled={locked}
                        className={`h-9 px-2.5 rounded-md border flex items-center gap-1.5 text-xs font-medium flex-shrink-0 ${locked ? "opacity-60 cursor-default" : ""} ${s.channel === "email" ? "border-blue-300 bg-blue-50 text-blue-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                        {s.channel === "email" ? <><Mail className="w-3.5 h-3.5" />E-mail</> : <><MessageCircle className="w-3.5 h-3.5" />WhatsApp</>}
                      </button>
                      {!locked && !s._fromAI && signers.filter(x => !x._fromTemplate && !x._fromAI).length > 1 && <button onClick={() => setSigners(p => p.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                    {s.channel === "email" ? <Input value={s.email} onChange={e => { if (locked) return; const u = [...signers]; u[i] = { ...u[i], email: e.target.value }; setSigners(u); }} placeholder="E-mail" className={`h-9 text-sm ${locked ? "bg-slate-50 text-slate-500" : "bg-white"}`} readOnly={locked} />
                    : <Input value={s.phone ? applyMask(s.phone.replace(/\D/g, "").replace(/^55/, ""), phoneMask) : ""} onChange={e => { if (locked) return; const u = [...signers]; u[i] = { ...u[i], phone: e.target.value.replace(/\D/g, "") }; setSigners(u); }} placeholder={applyMask("11999999999", phoneMask)} className={`h-9 text-sm ${locked ? "bg-slate-50 text-slate-500" : "bg-white"}`} readOnly={locked} />}
                  </div>
                  );
                })}
                <button onClick={() => setSigners(p => [...p, { name: "", channel: "email", email: "", phone: "" }])} className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1"><Plus className="w-3 h-3" />Adicionar</button>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setStep(hasValidation ? 1 : (isPrefilled ? 1 : 0))} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
                <div className="flex-1" />
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={!signers.some(s => s.name && (s.channel === "email" ? s.email : s.phone))} onClick={() => {
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  const phoneRegex = /^\d{10,13}$/;
                  for (const s of signers.filter(s => s.name)) {
                    if (s.channel === "email" && !emailRegex.test(s.email)) { toast.error(`E-mail inválido para ${s.name}: ${s.email || "(vazio)"}`); return; }
                    if (s.channel === "whatsapp" && !phoneRegex.test((s.phone || "").replace(/\D/g, ""))) { toast.error(`Telefone inválido para ${s.name}. Informe com DDD.`); return; }
                  }
                  setStep(3);
                }}><span className="flex items-center gap-1">Avançar<ArrowRight className="w-4 h-4" /></span></Button>
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {step === 3 && (
            <div className="space-y-3 flex-1 flex flex-col">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}><FileSignature className="w-4 h-4" style={{ color: tc500 }} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{neg?.titulo || "—"}</p>
                    <p className="text-[11px] text-slate-400">{ct?.nome || ""}{ct?.nome && emp?.nome ? " · " : ""}{emp?.nome || ""}</p>
                  </div>
                </div>
                {aiResult?.approved && <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg"><Check className="w-4 h-4 text-emerald-600" /><span className="text-xs font-medium text-emerald-700">Documentos validados pela IA</span></div>}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Signatários</p>
                  <div className="space-y-1.5">
                    {signers.filter(s => s.name).map((s, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${s.channel === "email" ? "bg-blue-50" : "bg-emerald-50"}`}>
                          {s.channel === "email" ? <Mail className="w-3.5 h-3.5 text-blue-500" /> : <MessageCircle className="w-3.5 h-3.5 text-emerald-500" />}
                        </div>
                        <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800">{s.name}</p><p className="text-[11px] text-slate-400">{s.channel === "email" ? s.email : applyMask((s.phone || "").replace(/^55/, ""), phoneMask)}</p></div>
                        <Badge className={`border-0 text-[9px] ${s.channel === "email" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{s.channel === "email" ? "E-mail" : "WhatsApp"}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setStep(2)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
                <div className="flex-1" />
                <Button variant="outline" className="gap-1.5" onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/autentique/preview-contract`, { template_id: form.template_id, negocio_id: form.negocio_id, ai_validation_data: aiResult?.data || {} });
                    if (r.data.html) { const w = window.open("", "_blank"); w.document.write(r.data.html); w.document.close(); }
                    else toast.error("Preview não disponível");
                  } catch { toast.error("Erro ao gerar preview"); }
                }}><Eye className="w-4 h-4" />Visualizar</Button>
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={sending} onClick={sendContract}>
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Enviar contrato
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Detail */}
      {aiDetailOpen && createPortal(<div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50" onClick={() => setAiDetailOpen(false)}>
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3"><p className="text-sm font-semibold flex items-center gap-2"><Brain className="w-4 h-4" style={{ color: tc500 }} />Detalhes</p><button onClick={() => setAiDetailOpen(false)} className="text-slate-400"><X className="w-4 h-4" /></button></div>
          {aiResult?.data && <JsonRenderer data={aiResult.data} />}
        </div>
      </div>, document.body)}

      {/* File Picker */}
      {filePickerOpen && createPortal(<div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50" onClick={() => setFilePickerOpen(false)}>
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[60vh] overflow-y-auto p-4" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3"><p className="text-sm font-semibold flex items-center gap-2"><Folder className="w-4 h-4" style={{ color: tc500 }} />Selecionar arquivo</p><button onClick={() => setFilePickerOpen(false)} className="text-slate-400"><X className="w-4 h-4" /></button></div>
          {filePickerList.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Nenhum arquivo.</p> : filePickerList.map(f => (
            <button key={f.id} onClick={() => { setDocUploads(p => ({ ...p, [filePickerIdx]: { ...f, _existing: true, name: f.original_filename } })); setFilePickerOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-left mb-1">
              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0"><span className="text-[9px] font-bold text-slate-500">{(f.original_filename?.split(".").pop() || "").toUpperCase()}</span></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-700 truncate">{f.original_filename}</p></div>
            </button>
          ))}
        </div>
      </div>, document.body)}

      {/* AI Review Modal */}
      <AIReviewModal
        open={aiReviewOpen}
        onClose={() => setAiReviewOpen(false)}
        aiData={aiResult?.data || {}}
        negocioId={form.negocio_id || prefillNegocioId}
        onConfirmed={() => {
          setAiResult(prev => prev ? { ...prev, _pendingReview: false } : prev);
          setAiReviewOpen(false);
        }}
      />

      {/* CNPJ Correction Modal */}
      {cnpjModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-rose-100">
                <AlertCircle className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">CNPJ inválido</p>
                <p className="text-xs text-slate-500">Corrija o CNPJ para continuar.</p>
              </div>
            </div>
            <div className="p-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">{cnpjModalError}</div>
            <div>
              <Label className="text-sm font-medium text-slate-700">CPF/CNPJ</Label>
              <Input
                value={(() => { const d = (cnpjOverride || "").replace(/\D/g, ""); if (d.length <= 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, a, b, c, e) => e ? `${a}.${b}.${c}-${e}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a); return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, e, f) => f ? `${a}.${b}.${c}/${e}-${f}` : e ? `${a}.${b}.${c}/${e}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a); })()}
                onChange={e => setCnpjOverride(e.target.value.replace(/\D/g, "").slice(0, 14))}
                placeholder="00.000.000/0000-00"
                className="mt-1 h-9 text-sm bg-white"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setCnpjModalOpen(false)}>Cancelar</Button>
              <Button className="flex-1 text-white" style={{ backgroundColor: tc500 }} disabled={validatingCnpj || cnpjOverride.replace(/\D/g, "").length < 11} onClick={async () => {
                const digits = cnpjOverride.replace(/\D/g, "");
                if (digits.length === 14) {
                  setValidatingCnpj(true);
                  try {
                    const res = await axios.get(`${API}/validate-cnpj/${digits}`);
                    if (!res.data.valid) { setCnpjModalError(res.data.error || "CNPJ não encontrado."); setValidatingCnpj(false); return; }
                    if (!res.data.ativa) { setCnpjModalError(`Empresa com situação: ${res.data.situacao}.`); setValidatingCnpj(false); return; }
                    toast.success(`CNPJ válido: ${res.data.razao_social}`);
                  } catch { /* allow */ }
                  setValidatingCnpj(false);
                }
                // Save corrected CNPJ to deal
                if (cnpjOverride) {
                  const negId = form.negocio_id || prefillNegocioId || negocioData?.id;
                  if (negId) axios.post(`${API}/asaas/save-cnpj-override/${negId}`, { cnpj: cnpjOverride }).catch(() => {});
                }
                setCnpjModalOpen(false);
                setStep(2);
              }}>
                {validatingCnpj ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Validar e avançar
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>,
    document.body
  );
}
